// Admin → Email ingest (docs/report-inbox.md): the routing table of the emailed-report
// inbox (which sender / subject / attachment lands in which practice as which report
// kind) and the record of what the job handled. super_admin only, like the endpoints.
//
// Routes: a table, an add/edit modal, enable/disable, delete with confirm, and "Load
// defaults", which POSTs the PracticeSuite + athenaNet defaults the service declares
// (skipping any (practice, kind) already here; they land disabled, their sender a
// placeholder). Messages: counts and statuses only — sender, subject, attachment NAME,
// kind, rows; never a report's contents.

import React, { useCallback, useEffect, useState } from 'react';
import './ReportInboxAdmin.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const BASE = `${API_URL}/api/admin/report-inbox`;

const EMPTY_FORM = {
  practice_id: '',
  sender_match: '',
  subject_regex: '',
  attachment_regex: '',
  kind: '',
  enabled: true,
  note: '',
};

async function readJson(response) {
  let body = null;
  try {
    body = await response.json();
  } catch (e) {
    body = null;
  }
  if (!response.ok) {
    throw new Error((body && body.error) || `Request failed (${response.status})`);
  }
  return body;
}

function fmtStamp(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

export default function ReportInboxAdmin({ practices }) {
  const [routes, setRoutes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [modalError, setModalError] = useState(null);
  const [saving, setSaving] = useState(false);

  const practiceName = (id) => {
    const p = (practices || []).find((x) => x.id === id);
    return p ? p.name : `Practice ${id}`;
  };
  const kinds = Array.from(new Set([...defaults.map((d) => d.kind), ...routes.map((r) => r.kind)])).sort();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, m, d] = await Promise.all([
        fetch(`${BASE}/routes`, { credentials: 'include' }).then(readJson),
        fetch(`${BASE}/messages`, { credentials: 'include' }).then(readJson),
        fetch(`${BASE}/defaults`, { credentials: 'include' }).then(readJson),
      ]);
      setRoutes(r.routes || []);
      setMessages(m.messages || []);
      setDefaults(d.defaults || []);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, practice_id: practices && practices.length ? String(practices[0].id) : '' });
    setModalError(null);
    setShowModal(true);
  };

  const openEdit = (route) => {
    setEditing(route);
    setForm({
      practice_id: String(route.practice_id),
      sender_match: route.sender_match || '',
      subject_regex: route.subject_regex || '',
      attachment_regex: route.attachment_regex || '',
      kind: route.kind,
      enabled: !!route.enabled,
      note: route.note || '',
    });
    setModalError(null);
    setShowModal(true);
  };

  const save = async () => {
    setSaving(true);
    setModalError(null);
    const body = {
      practice_id: Number(form.practice_id),
      sender_match: form.sender_match,
      subject_regex: form.subject_regex,
      attachment_regex: form.attachment_regex,
      kind: form.kind,
      enabled: !!form.enabled,
      note: form.note,
    };
    try {
      const url = editing ? `${BASE}/routes/${editing.id}` : `${BASE}/routes`;
      await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then(readJson);
      setShowModal(false);
      setMessage({ type: 'success', text: editing ? 'Route updated.' : 'Route added.' });
      load();
    } catch (err) {
      setModalError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (route) => {
    try {
      await fetch(`${BASE}/routes/${route.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !route.enabled }),
      }).then(readJson);
      load();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const remove = async (route) => {
    if (!window.confirm(`Delete the ${route.kind} route for ${practiceName(route.practice_id)}?`)) return;
    try {
      await fetch(`${BASE}/routes/${route.id}`, { method: 'DELETE', credentials: 'include' }).then(readJson);
      setMessage({ type: 'success', text: 'Route deleted.' });
      load();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const loadDefaults = async () => {
    try {
      const counts = await fetch(`${BASE}/routes/defaults`, { method: 'POST', credentials: 'include' }).then(readJson);
      setMessage({
        type: 'success',
        text: `Defaults: ${counts.added} added, ${counts.skipped} already here, ${counts.no_practice} without a practice. New rows are disabled until their sender is filled in.`,
      });
      load();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  return (
    <div className="report-inbox-admin">
      <div className="section-header">
        <h3 className="section-title">Email ingest routes</h3>
        <div className="report-inbox-actions">
          <button className="btn btn-secondary btn-sm" onClick={loadDefaults} disabled={loading}>
            Load defaults
          </button>
          <button className="btn btn-primary btn-sm" onClick={openAdd}>
            Add route
          </button>
        </div>
      </div>
      <p className="text-muted report-inbox-help">
        The first enabled route whose sender (substring), subject regex and attachment regex all match wins.
        A sender starting with <code>TODO-</code> is a placeholder: fill it from the dry run, then enable the route.
      </p>

      {message && (
        <div className={`form-message ${message.type} dismissable`}>
          <span className="message-content">{message.text}</span>
          <button className="message-close" onClick={() => setMessage(null)}>&times;</button>
        </div>
      )}

      {loading ? (
        <div className="loading-state"><div className="spinner"></div>Loading...</div>
      ) : routes.length === 0 ? (
        <div className="empty-message">No routes yet. Load the defaults or add one.</div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="detail-table">
            <thead>
              <tr>
                <th>Practice</th>
                <th>Sender contains</th>
                <th>Subject regex</th>
                <th>Attachment regex</th>
                <th>Kind</th>
                <th>Enabled</th>
                <th>Note</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {routes.map((r) => (
                <tr key={r.id} className={r.enabled ? '' : 'report-inbox-row-off'}>
                  <td>{practiceName(r.practice_id)}</td>
                  <td><code>{r.sender_match || '(any)'}</code></td>
                  <td><code>{r.subject_regex || '(any)'}</code></td>
                  <td><code>{r.attachment_regex || '(any)'}</code></td>
                  <td>{r.kind}</td>
                  <td>
                    <span className={`user-status-badge ${r.enabled ? 'active' : 'inactive'}`}>
                      {r.enabled ? 'On' : 'Off'}
                    </span>
                  </td>
                  <td className="report-inbox-note" title={r.note || ''}>{r.note || '—'}</td>
                  <td className="col-actions">
                    <button className="btn-link" onClick={() => openEdit(r)}>Edit</button>
                    <button className={`btn-link ${r.enabled ? 'warning' : 'success'}`} onClick={() => toggle(r)}>
                      {r.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button className="btn-link warning" onClick={() => remove(r)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="section-header report-inbox-messages-head">
        <h3 className="section-title">Recent messages</h3>
      </div>
      {!loading && messages.length === 0 ? (
        <div className="empty-message">The job has not handled an attachment yet.</div>
      ) : !loading && (
        <div className="admin-table-wrapper">
          <table className="detail-table">
            <thead>
              <tr>
                <th>Received</th>
                <th>Sender</th>
                <th>Subject</th>
                <th>Attachment</th>
                <th>Practice</th>
                <th>Kind</th>
                <th>Status</th>
                <th>Rows</th>
              </tr>
            </thead>
            <tbody>
              {messages.map((m) => (
                <tr key={m.id}>
                  <td title={m.processed_at || ''}>{fmtStamp(m.received_at)}</td>
                  <td>{m.sender || '—'}</td>
                  <td className="report-inbox-note" title={m.subject || ''}>{m.subject || '—'}</td>
                  <td>{m.attachment_name || '—'}</td>
                  <td>{m.practice_id ? practiceName(m.practice_id) : '—'}</td>
                  <td>{m.kind || '—'}</td>
                  <td>
                    <span className={`report-inbox-status status-${m.status}`} title={(m.result && m.result.error) || ''}>
                      {m.status}
                    </span>
                  </td>
                  <td>{m.rows === null || m.rows === undefined ? '—' : m.rows}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="create-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editing ? 'Edit route' : 'Add route'}</h2>
            {modalError && <div className="form-message error">{modalError}</div>}
            <div className="form-row">
              <div className="form-field">
                <label>Practice</label>
                <select value={form.practice_id} onChange={(e) => setForm({ ...form, practice_id: e.target.value })}>
                  {(practices || []).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Kind</label>
                <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                  <option value="">Choose a kind</option>
                  {kinds.map((k) => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-field full">
                <label>Sender contains</label>
                <input type="text" value={form.sender_match} placeholder="reports@example.invalid"
                  onChange={(e) => setForm({ ...form, sender_match: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>Subject regex</label>
                <input type="text" value={form.subject_regex} onChange={(e) => setForm({ ...form, subject_regex: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Attachment regex</label>
                <input type="text" value={form.attachment_regex} onChange={(e) => setForm({ ...form, attachment_regex: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field full">
                <label>Note</label>
                <input type="text" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <label className="report-inbox-checkbox">
                <input type="checkbox" checked={!!form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
                Enabled
              </label>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving || !form.kind || !form.practice_id}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
